// Parsing/labels de reward compartilhados entre o card antigo
// (components/user/rewards/Item.vue) e o novo card do Minha Conta V2
// (components/user-v2/RewardCard.vue) — evita duplicar a mesma regra em
// dois lugares.

// player_desc chega como "Giros grátis Tigre Sortudo {R$ 0,05}": o valor
// (quando existe) vem embutido entre chaves, resto é a descrição exibida.
export function parseRewardDescription(playerDesc?: string) {
  const match = playerDesc?.match(/{([^}]+)}/);

  return {
    value: match?.[1]?.trim() ?? null,
    description: playerDesc?.replace(/{[^}]+}/, '').trim() || '',
  };
}

// tom semântico por status — único lugar que sabe "expired é vermelho,
// redeemed é verde" etc.; os dois design systems abaixo só traduzem o tom
// pras próprias classes.
type RewardTone = 'primary' | 'success' | 'error' | 'warning';

const STATUS_CONFIG: Record<
  RewardStatus,
  { text: string; tone: RewardTone; icon: string }
> = {
  available: { text: 'Disponível', tone: 'primary', icon: 'clock' },
  redeemed: { text: 'Resgatado', tone: 'success', icon: 'check' },
  auto_redeemed: {
    text: 'Resgate automático',
    tone: 'success',
    icon: 'check',
  },
  expired: { text: 'Expirado', tone: 'error', icon: 'timer' },
  canceled: { text: 'Cancelado', tone: 'error', icon: 'close' },
};

function getStatusConfig(status: RewardStatus) {
  // status é um union fechado (types/rewards.d.ts) — o fallback é só rede de
  // segurança caso a API mande algo fora do tipo.
  return (
    STATUS_CONFIG[status] ?? {
      text: status,
      tone: 'warning' as const,
      icon: 'info',
    }
  );
}

// classes por tom, escritas por extenso — Tailwind escaneia string literal
// no source, não dá pra montar "text-" + tom em runtime (o JIT simplesmente
// não geraria a classe).
const LEGACY_TONE_CLASSES: Record<
  RewardTone,
  { color: string; bg: string; border: string }
> = {
  primary: {
    color: 'text-primary',
    bg: 'bg-primary/5',
    border: 'border-primary/10',
  },
  success: {
    color: 'text-success',
    bg: 'bg-success/5',
    border: 'border-success/10',
  },
  error: { color: 'text-error', bg: 'bg-error/5', border: 'border-error/10' },
  warning: {
    color: 'text-warning',
    bg: 'bg-warning/5',
    border: 'border-warning/10',
  },
};

// mesmo tom do LEGACY_TONE_CLASSES acima, só que nos tokens v2-* — usada
// pelo card novo (components/user-v2/RewardCard.vue), que é 100% v2-* e não
// pode puxar cor do design system antigo. "error" legado = "danger" na v2
// (nomes diferentes pro mesmo tom em cada sistema).
const V2_TONE_CLASSES: Record<
  RewardTone,
  { color: string; bg: string; border: string }
> = {
  primary: {
    color: 'text-v2-primary',
    bg: 'bg-v2-primary/10',
    border: 'border-v2-primary/20',
  },
  success: {
    color: 'text-v2-success',
    bg: 'bg-v2-success/10',
    border: 'border-v2-success/20',
  },
  error: {
    color: 'text-v2-danger',
    bg: 'bg-v2-danger/10',
    border: 'border-v2-danger/20',
  },
  warning: {
    color: 'text-v2-warning',
    bg: 'bg-v2-warning/10',
    border: 'border-v2-warning/20',
  },
};

export function getRewardStatus(status: RewardStatus) {
  const { text, tone, icon } = getStatusConfig(status);

  return { text, icon, ...LEGACY_TONE_CLASSES[tone] };
}

export function getRewardStatusV2(status: RewardStatus) {
  const { text, tone, icon } = getStatusConfig(status);

  return { text, icon, ...V2_TONE_CLASSES[tone] };
}
